import React from "react";
import { Button, Tag, Typography } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import get from "lodash/get";

import { cabinetDetailsPath, orderDetailsPath } from "./routes";
import {
  ORDER_STATUS_COLORS,
  readableDateTime,
  serverSortProps,
} from "../utilities/helper";
import CopyText from "../components/CopyText";
import TimeAgo from "../components/TimeAgo";

const { Text } = Typography;

/**
 * APPROVED / UNAPPROVED CABINET TABLE
 */
export const approvedCabinetTable = ({
  tab,
  updateCabinet,
  onApproveCabinet,
  canUpdateCabinet,
  canApproveCabinet,
}) =>
  [
    {
      title: "Sr. No.",
      key: "serial_no",
      className: "col-index",
      width: 80,
      responsive: ["sm"],
      render: (_, __, index) => <Text>{index + 1}</Text>,
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Hardware ID",
      key: "hardware_id",
      dataIndex: "cabinet_sn",
      className: "col-mono",
      render: (item, record) => (
        <CopyText value={item}>
          <Link to={cabinetDetailsPath(get(record, "cabinet_sn"))}>
            {item}
          </Link>
        </CopyText>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "is_working",
      hidden: tab === "unapproved",
      render: (item) => (
        <Tag color={item ? "success" : "error"}>
          {item ? "Online" : "Offline"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      className: "col-actions",
      fixed: "right",
      hidden: tab === "unapproved" ? !canApproveCabinet : !canUpdateCabinet,
      render: (record) =>
        tab === "unapproved" ? (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => onApproveCabinet(get(record, "id"))}
          >
            Approve
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => updateCabinet(record)}
          >
            Edit
          </Button>
        ),
    },
  ].filter((column) => !column.hidden);

/**
 * CABINET OVERVIEW TABLE
 */
export const approvedOverviewTable = () => [
  {
    title: "Name",
    key: "name",
    dataIndex: "name",
  },
  {
    title: "Hardware ID",
    key: "hardware_id",
    dataIndex: "cabinet_sn",
    className: "col-mono",
    render: (item, record) => (
      <CopyText value={item}>
        <Link to={cabinetDetailsPath(get(record, "cabinet_sn"))}>
          {item}
        </Link>
      </CopyText>
    ),
  },
  {
    title: "Total Slots",
    key: "total_slots",
    dataIndex: "total_slots",
    align: "right",
    className: "col-num",
  },
  {
    title: "Battery Packs Present",
    key: "available_battery_pack",
    dataIndex: "available_battery_pack",
    align: "right",
    className: "col-num",
  },
  {
    title: "Empty Slots",
    key: "free_slots",
    dataIndex: "free_slots",
    align: "right",
    className: "col-num",
  },
];

/**
 * CABINET ORDER TABLE
 */
export const cabinetOrderTable = (offset, createdSortOrder = null) => [
  {
    title: "Sr. No",
    key: "serial_no",
    className: "col-index",
    width: 72,
    responsive: ["sm"],
    render: (_, __, index) => <Text>{offset + index + 1}</Text>,
  },
  {
    title: "Order ID",
    key: "order_id",
    dataIndex: "order_id",
    className: "col-mono",
    render: (item) => <CopyText value={item} />,
  },
  {
    title: "Order Status",
    key: "order_status",
    dataIndex: "order_status",
    render: (item) =>
      item ? (
        <Tag color={ORDER_STATUS_COLORS[item] || "default"}>{item}</Tag>
      ) : (
        item
      ),
  },
  {
    title: "Battery ID",
    key: "battery_id",
    dataIndex: "battery_identifier",
    className: "col-mono",
    responsive: ["md"],
  },
  {
    title: "Pickup Cabinet",
    key: "cabinet_name",
    dataIndex: "cabinet_name",
  },
  {
    title: "Return Cabinet",
    key: "return_cabinet_name",
    dataIndex: "return_cabinet_name",
    responsive: ["md"],
  },
  {
    title: "Order Amount",
    key: "order_amount",
    dataIndex: "order_amount",
    align: "right",
    className: "col-num",
    render: (item) => <Text>{item ? `$${item}` : "--"}</Text>,
  },
  {
    title: "Order Created",
    key: "created_at",
    dataIndex: "created_at",
    className: "col-date",
    // Sorted on the server (see Orders component)
    sorter: true,
    sortOrder: createdSortOrder,
    sortDirections: ["ascend", "descend"],
    render: (item) => <TimeAgo value={item} />,
  },
  {
    title: "Action",
    key: "action",
    align: "right",
    className: "col-actions",
    fixed: "right",
    render: (_, record) => (
      <Link to={orderDetailsPath(get(record, "order_id"))}>
        <Button type="primary" icon={<ArrowRightOutlined />}>
          Details
        </Button>
      </Link>
    ),
  },
];

/**
 * INVOICE REPORT TABLE
 */
export const invoiceReportTable = (offset, sort = null) => [
  {
    title: "Sr. No",
    key: "serial_no",
    className: "col-index",
    width: 72,
    responsive: ["sm"],
    render: (_, __, index) => <Text>{offset + index + 1}</Text>,
  },
  {
    title: "Cabinet Identifier",
    key: "cabinet_identifier",
    dataIndex: "cabinet_identifier",
    className: "col-mono",
    render: (item) => <CopyText value={item} />,
  },
  {
    title: "Start Date",
    key: "start_date",
    dataIndex: "start_date",
    className: "col-date",
    render: (item) =>
      item ? readableDateTime(Number(item), "dd/mm/yyyy hh:mm") : "--",
  },
  {
    title: "End Date",
    key: "end_date",
    dataIndex: "end_date",
    className: "col-date",
    render: (item) =>
      item ? readableDateTime(Number(item), "dd/mm/yyyy hh:mm") : "--",
  },
  {
    title: "Created At",
    key: "created_at",
    dataIndex: "created_at",
    className: "col-date",
    // Sorted on the server (see InvoiceReport component)
    ...serverSortProps(sort, "created_at"),
    render: (item) => <TimeAgo value={item} />,
  },
  {
    title: "Updated At",
    key: "updated_at",
    dataIndex: "updated_at",
    className: "col-date",
    responsive: ["md"],
    ...serverSortProps(sort, "updated_at"),
    render: (item) => <TimeAgo value={item} />,
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "url",
    align: "right",
    className: "col-actions",
    fixed: "right",
    render: (item) =>
      item ? (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => window.open(item)}
        >
          Download
        </Button>
      ) : (
        "--"
      ),
  },
];
